import React, { memo, ChangeEvent, useEffect, useContext } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import orderBy from 'lodash/orderBy'
import uniqBy from 'lodash/uniqBy'
import moment from 'moment'
import Skeleton from 'transfix-ui/core/Skeleton/Skeleton'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import * as yup from 'yup'
import { ShareLoadLinkPreview } from 'apps/fleet-planner/components/share-load/ShareLoadLinkPreview'
import { ShareLoadTable } from 'apps/fleet-planner/components/share-load/ShareLoadTable'
import { ShareLoadContext } from 'apps/fleet-planner/context'
import * as Types from 'apps/fleet-planner/types'
import { Button, PhoneInput, TextArea } from 'transfix-ui'
import { FORMATTED_PHONE_REGEX, formatPhone, unformatPhone } from 'utils/formatPhone'
import { FORMATS } from 'constants/datetime'

/**
 * Returns a sentence that is used in the default state
 * of the `notes` input element
 * @param {ILoad} load
 * @returns {string}
 */
const getDefaultNotesSentence = (load?: Types.ILoad): string => {
  const { stops = [] } = load || {}
  const [pickup] = stops || []

  const { displayTimezone, startAt } = pickup || {}
  // if timezone or startAt isn't present return ''
  if (!displayTimezone || !startAt) {
    return ''
  }
  const formattedDate = moment(startAt).tz(displayTimezone).format(FORMATS.fpSMSDate) // Tue 8/11, 12:00am EDT
  const stopsPart = stops.map(({ city, stateCode }) => `${city}, ${stateCode}`).join(' - ')
  return `This is your dispatcher letting you know that you’re assigned to ${stopsPart}, pickup ${formattedDate}`
}

/**
 * Ensure that phone number has no (+1) area code
 * and remove any accidental formatting with () or -
 * @param {string} phoneNumber
 */
export const getDefaultPhoneNumber = (phoneNumber?: string | null): string => {
  // Handle null values
  const phone = phoneNumber ?? ''

  // Replace area code and symbol
  const withoutAreaCode = phone.replace('+1', '')

  // Remove extra formatting
  return unformatPhone(withoutAreaCode)
}

// Maximum length for sending an SMS message
const MAX_NOTES_LENGTH = 160

export const ShareLoadFormSchema = yup
  .object({
    contactInformation: yup.object({
      cellPhone: yup
        .string()
        .matches(FORMATTED_PHONE_REGEX, 'Phone number is not valid')
        .required('This field is required'),
    }),
    notes: yup
      .string()
      .max(
        MAX_NOTES_LENGTH,
        `Your message can only contain ${MAX_NOTES_LENGTH} characters or less.`
      )
      .required('This field is required.'),
  })
  .required()

export type ShareLoadFormSchemaType = yup.InferType<typeof ShareLoadFormSchema>

interface IShareLoadFormProps {
  selectedDriver: Types.IDriver
  share: Types.IShare | undefined
  shipment?: Types.ILoad
  isLoading: boolean
  onCancel: () => void
  onSubmit: (data: ShareLoadFormSchemaType, shouldUpdateDriver: boolean) => void
  isLoadAssignable?: boolean
  isSelectedDriverAssigned?: boolean
  drivers?: Types.IDriver[]
}

export const ShareLoadForm = memo<IShareLoadFormProps>(
  ({
    drivers,
    isLoadAssignable,
    isLoading,
    isSelectedDriverAssigned,
    onCancel,
    onSubmit,
    selectedDriver,
    share,
    shipment,
  }) => {
    const { cellPhone: driverCellPhone, id: selectedDriverId } = selectedDriver || {}
    const { id: shipmentId } = shipment || {}

    const { shipmentShares = [], isLoading: isSharesLoading } = useContext(ShareLoadContext)
    const { getFlag } = useFeatureFlagContext()
    const { enabled: isFmsOneTimeSmsLoadShareEnabled } = getFlag('fmsOneTimeSmsLoadShare')
    const cellPhone = getDefaultPhoneNumber(driverCellPhone)

    const UNASSIGNED_LOAD_MESSAGE = `This is your dispatcher, here are the details for the load #${shipmentId}. You have not been assigned to this load yet.`
    const isDriverAssignOrAssignable = isLoadAssignable || isSelectedDriverAssigned
    const loadMessage = isDriverAssignOrAssignable
      ? getDefaultNotesSentence(shipment)
      : UNASSIGNED_LOAD_MESSAGE

    const { control, errors, handleSubmit, reset } = useForm({
      mode: 'all',
      resolver: yupResolver(ShareLoadFormSchema),
      defaultValues: ShareLoadFormSchema.cast({
        contactInformation: {
          cellPhone: formatPhone(cellPhone, { removeCountryCode: '+1' }),
        },
        notes: loadMessage,
      }),
    })

    const filteredShares = uniqBy(
      orderBy(
        shipmentShares.filter(({ lastSharedAt }: Types.IShare): boolean => !!lastSharedAt),
        'lastSharedAt',
        'desc'
      ),
      (share: Types.IShare) => share.sharedWithDriver.id
    )

    // If new data, reset the form, i.e. when driver changes
    useEffect(() => {
      if (selectedDriver) {
        reset(
          ShareLoadFormSchema.cast({
            contactInformation: {
              cellPhone: formatPhone(cellPhone, {
                removeCountryCode: '+1',
              }),
            },
            notes: loadMessage,
          })
        )
      }
    }, [selectedDriver, reset, cellPhone, shipment, loadMessage])

    /**
     * Send form data to parent component
     * @param {ShareLoadFormSchemaType} data
     */
    const submitHandler = (data: ShareLoadFormSchemaType): void => {
      const { contactInformation } = data || {}
      const { cellPhone: cellPhoneInput } = contactInformation || {}
      const isNewPhoneNumber = cellPhone !== cellPhoneInput
      onSubmit(data, isNewPhoneNumber)
    }

    return (
      <form className='share-load-form' onSubmit={handleSubmit(submitHandler)}>
        {isFmsOneTimeSmsLoadShareEnabled && selectedDriverId && (
          <>
            {/* Phone number */}
            <div className='row'>
              <div className='col-md-6 col-lg-3'>
                <Controller
                  control={control}
                  name='contactInformation.cellPhone'
                  render={({ onBlur, onChange, value }) => {
                    return (
                      <PhoneInput
                        placeholder='(000)-000-0000'
                        clearable={false}
                        autoComplete='off'
                        dataTest='share-load-phone'
                        large
                        required
                        label='Driver Mobile Phone'
                        value={value}
                        autoFocus={!value || value?.length === 0}
                        onBlur={onBlur}
                        onChange={({ target: { value: val } }: ChangeEvent<HTMLInputElement>) => {
                          onChange(val)
                        }}
                        invalid={!!errors?.contactInformation?.cellPhone}
                        validationMessage={errors?.contactInformation?.cellPhone?.message}
                        className='fs-mask'
                      />
                    )
                  }}
                />
              </div>
            </div>

            {/* Notes */}
            <div className='row mb2 mt2'>
              <div className='col-md-12'>
                <Controller
                  control={control}
                  name='notes'
                  render={({ onBlur, onChange, value }) => {
                    return (
                      <div className={errors?.notes?.message && 'invalid'}>
                        <TextArea
                          dataTest='share-load-notes'
                          size='medium'
                          required
                          label='Message (150 characters or less)'
                          placeholder='Write something...'
                          onBlur={onBlur}
                          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                            onChange(e.target.value)
                          }}
                          value={value}
                          invalid={!!errors?.notes?.message}
                        />
                        {errors?.notes?.message && (
                          <span className='validation-message'>{errors?.notes?.message}</span>
                        )}
                      </div>
                    )
                  }}
                />
              </div>
            </div>
            {isLoading ? (
              <Skeleton height={100} />
            ) : (
              <div className='row mb2 mt2'>
                <div className='col-md-12'>
                  {share && <ShareLoadLinkPreview url={share?.url} />}
                </div>
              </div>
            )}
          </>
        )}
        {isFmsOneTimeSmsLoadShareEnabled && filteredShares.length > 0 && (
          <>
            <div className='share-load-modal-separator' />
            {isLoading || isSharesLoading ? (
              <Skeleton height={150} />
            ) : (
              <section className='mb5'>
                <ShareLoadTable shares={filteredShares} drivers={drivers} />
              </section>
            )}
          </>
        )}
        {isFmsOneTimeSmsLoadShareEnabled && (
          <section>
            <div className='share-load-modal-separator' />

            <div className='row'>
              <div className='col-md-12 tr'>
                <Button color='tertiary' size='large' onClick={onCancel}>
                  Cancel
                </Button>
                <Button
                  color='primary'
                  size='large'
                  className='ml2'
                  data-test='share-load-submit-form-button'
                  disabled={isLoading || !selectedDriverId} // prevent user from submitting without selecting a driver
                  onClick={handleSubmit(submitHandler)}>
                  Send
                </Button>
              </div>
            </div>
          </section>
        )}
        {!isFmsOneTimeSmsLoadShareEnabled && (
          <>
            {/* Phone number */}
            <div className='row'>
              <div className='col-md-6 col-lg-3'>
                <Controller
                  control={control}
                  name='contactInformation.cellPhone'
                  render={({ onBlur, onChange, value }) => {
                    return (
                      <PhoneInput
                        placeholder='(000)-000-0000'
                        clearable={false}
                        autoComplete='off'
                        dataTest='share-load-phone'
                        large
                        required
                        label='Driver Mobile Phone'
                        value={value}
                        autoFocus={!value || value?.length === 0}
                        onBlur={onBlur}
                        onChange={({ target: { value: val } }: ChangeEvent<HTMLInputElement>) => {
                          onChange(val)
                        }}
                        invalid={!!errors?.contactInformation?.cellPhone}
                        validationMessage={errors?.contactInformation?.cellPhone?.message}
                        className='fs-mask'
                      />
                    )
                  }}
                />
              </div>
            </div>

            {/* Notes */}
            <div className='row mb2'>
              <div className='col-md-12'>
                <Controller
                  control={control}
                  name='notes'
                  render={({ onBlur, onChange, value }) => {
                    return (
                      <div className={errors?.notes?.message && 'invalid'}>
                        <TextArea
                          dataTest='share-load-notes'
                          size='medium'
                          required
                          label='Message (160 characters or less)'
                          placeholder='Write something...'
                          onBlur={onBlur}
                          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                            onChange(e.target.value)
                          }}
                          value={value}
                          invalid={!!errors?.notes?.message}
                        />
                        {errors?.notes?.message && (
                          <span className='validation-message'>{errors?.notes?.message}</span>
                        )}
                      </div>
                    )
                  }}
                />
              </div>
            </div>
            <div className='row mb2'>
              <div className='col-md-12'>{share && <ShareLoadLinkPreview url={share?.url} />}</div>
            </div>
            <div className='row'>
              <div className='col-md-12 tr'>
                <Button color='tertiary' size='large' onClick={onCancel}>
                  Cancel
                </Button>
                <Button
                  color='primary'
                  size='large'
                  className='ml2'
                  data-test='share-load-submit-form-button'
                  disabled={isLoading}
                  onClick={handleSubmit(submitHandler)}>
                  Send
                </Button>
              </div>
            </div>
          </>
        )}
      </form>
    )
  }
)
